import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from 'vuex-module-decorators'
import { ITournamentState } from '@/store/types'
import {
  ConfirmTournamentTeamRosterRequest,
  ConfirmTournamentTeamRosterStatus,
  CreateTournamentTeamRequest,
  MemberJoinedTournamentsEntity,
  ResponseStatus,
  TournamentAPI,
  TournamentEntity,
  TournamentEntityPagination,
  TournamentState
} from '@/api/tournament'
import {
  TournamentPlayerEntity,
  TournamentTeamEntity,
  TournamentTeamResponsePagination,
  TournamentTeamState
} from '@/api/tournamentTeam'
import { StageEntity } from '@/api/stage'
import store from '@/store'
import ServerError from '@/utils/server_error'
import { isEqual } from 'lodash'
import paymentStore from '@/modules/payment/payment.store'
import { PaymentAPI } from '@/api/payment'
import lottie from 'lottie-web'
import checkTransactionStatus from '@/utils/checkTransactionStatus'
import { CancellationToken } from '@/utils/cancel'

export interface TournamentCatalogSearchFilter {
  state: TournamentState | TournamentState[] | undefined
  game: string | undefined
  venueId: string | undefined
  venueCompanyId: string | undefined
  isAutomated: boolean
}

@Module({ dynamic: true, store: store, namespaced: true, name: 'tournament' })
export default class TournamentModule extends VuexModule
  implements ITournamentState {
  tournamentCatalog: TournamentEntity[] = []
  tournamentCatalogCurrentPage: number = 0
  tournamentCatalogHasMorePage: boolean = true
  tournamentCatalogLoading: boolean = false
  tournamentCatalogSearchFilter: TournamentCatalogSearchFilter | {} = {}
  featuredTournamentCatalogIndex: number = -1
  joinedTournaments: MemberJoinedTournamentsEntity[] = []
  selectedTournament: TournamentEntity | null = null
  selectedTournamentTeam: TournamentTeamEntity | null = null
  selectedTournamentPlayerList: TournamentTeamResponsePagination = {
    items: [],
    hasMorePage: false
  }
  confirmedTournamentPlayers: boolean = false
  selectedTournamentStages: StageEntity[] | null = null
  tournamentActionDialogVisible: boolean = false
  tournamentRegistrationClosedDialogVisible: boolean = false
  joinedTournamentTeamLoading: boolean = false
  tournamentRegisteredTeamCount: number = 0

  // Automated tournament dialog visibility
  isInstantMatchIntroductionDialogVisible: boolean = false
  isAutomatedTournamentRuleDialogVisible: boolean = false
  isAutomatedTournamentTeamListDialogVisible: boolean = false
  isAutomatedTournamentBracketDialogVisible: boolean = false
  isTournamentCheckInOverlayVisible: boolean = false

  @Mutation
  CLEAR_TOURNAMENT_CATALOG() {
    this.tournamentCatalogCurrentPage = 0
    this.tournamentCatalogHasMorePage = true
    this.tournamentCatalog = []
    // this.joinedTournaments = []
  }

  @Mutation
  SET_TOURNAMENT_ACTION_DIALOG_VISIBLE(visible: boolean = false) {
    this.tournamentActionDialogVisible = visible
  }

  @Mutation
  SET_TOURNAMENT_REGISTRATION_CLOSED_DIALOG_VISIBLE(visible: boolean = false) {
    this.tournamentRegistrationClosedDialogVisible = visible
  }

  @Mutation
  SET_TOURNAMENT_CATALOG_CURRENT_PAGE(page: number) {
    this.tournamentCatalogCurrentPage = page
  }

  @Mutation
  SET_TOURNAMENT_CATALOG(tournaments: TournamentEntityPagination) {
    this.tournamentCatalogHasMorePage = tournaments.hasMorePage
    this.tournamentCatalog = [...this.tournamentCatalog, ...tournaments.items]
    this.tournamentCatalogLoading = false
  }

  @Action
  setTournamentActionDialogVisible(visible: boolean = false) {
    this.context.commit('SET_TOURNAMENT_ACTION_DIALOG_VISIBLE', visible)
  }

  @Mutation
  SET_TOURNAMENT_CATALOG_LOADING() {
    this.tournamentCatalogLoading = true
  }

  @Mutation
  SET_JOINED_TOURNAMENTS(joinedTournaments: MemberJoinedTournamentsEntity[]) {
    this.joinedTournaments = joinedTournaments
  }

  @Mutation
  SET_SELECTED_TOURNAMENT(tournament: TournamentEntity) {
    this.selectedTournament = tournament
    this.tournamentActionDialogVisible = false
  }

  @Mutation
  SET_FEATURED_TOURNAMENT_CATALOG_INDEX(index: number) {
    this.featuredTournamentCatalogIndex = index
  }

  @Mutation
  SET_SELECTED_TOURNAMENT_TEAM(tournamentTeam: TournamentTeamEntity) {
    this.selectedTournamentTeam = tournamentTeam
  }

  @Mutation
  RESET_SELECTED_TOURNAMENT_TEAM() {
    this.selectedTournamentTeam = null
  }

  @Mutation
  SET_SELECTED_TOURNAMENT_TEAM_LIST(
    tournamentTeams: TournamentTeamResponsePagination
  ) {
    if (this.selectedTournamentPlayerList.items.length === 0) {
      this.selectedTournamentPlayerList = tournamentTeams
    } else {
      this.selectedTournamentPlayerList.items = [
        ...this.selectedTournamentPlayerList.items,
        ...tournamentTeams.items
      ]
      this.selectedTournamentPlayerList.hasMorePage =
        tournamentTeams.hasMorePage
    }
  }

  @Mutation
  RESET_SELECTED_TOURNAMENT_TEAM_LIST() {
    this.selectedTournamentPlayerList = {
      items: [],
      hasMorePage: false
    }
  }

  @Mutation
  SET_CONFIRMED_TOURNAMENT_TEAM_ROSTER(confirmed: boolean) {
    this.confirmedTournamentPlayers = confirmed
  }

  @Mutation
  SET_SELECTED_TOURNAMENT_STAGES(stages: StageEntity[]) {
    this.selectedTournamentStages = stages
  }

  @Mutation
  SET_TOURNAMENT_CATALOG_FILTER(filter: TournamentCatalogSearchFilter | {}) {
    this.tournamentCatalogSearchFilter = filter
  }

  @Mutation
  SET_TOURNAMENT_REGISTERED_TEAM_COUNT(count: number) {
    this.tournamentRegisteredTeamCount = count
  }

  @Mutation
  SET_AUTOMATED_TOURNAMENT_INTRODUCTION_DIALOG_VISIBLE(value: boolean) {
    this.isInstantMatchIntroductionDialogVisible = value
  }

  @Mutation
  SET_AUTOMATED_TOURNAMENT_RULE_DIALOG_VISIBLE(value: boolean) {
    this.isAutomatedTournamentRuleDialogVisible = value
  }

  @Mutation
  SET_AUTOMATED_TOURNAMENT_TEAM_LIST_DIALOG_VISIBLE(value: boolean) {
    this.isAutomatedTournamentTeamListDialogVisible = value
  }

  @Mutation
  SET_AUTOMATED_TOURNAMENT_BRACKET_DIALOG_VISIBLE(value: boolean) {
    this.isAutomatedTournamentBracketDialogVisible = value
  }

  @Mutation
  SET_AUTOMATED_TOURNAMENT_CHECK_IN_OVERLAY_VISIBLE(value: boolean) {
    this.isTournamentCheckInOverlayVisible = value
  }

  @Mutation
  SET_JOINED_TOURNAMENT_TEAM_LOADING(value: boolean) {
    this.joinedTournamentTeamLoading = value
  }

  @Action
  async clearTournamentCatalog() {
    this.context.commit('CLEAR_TOURNAMENT_CATALOG')
  }

  @Action
  async setFeaturedTournamentIndex(index: number = -1) {
    this.context.commit('SET_FEATURED_TOURNAMENT_CATALOG_INDEX', index)
  }

  @Action({ rawError: true })
  async setTournamentCatalogFilter(
    filter: TournamentCatalogSearchFilter | {} = {}
  ) {
    // if requesting catalog filter value is NOT the same as vuex stored.
    if (!isEqual(this.tournamentCatalogSearchFilter, filter)) {
      await this.clearTournamentCatalog()
    }
    this.context.commit('SET_TOURNAMENT_CATALOG_FILTER', filter)
  }

  @Action({ rawError: true })
  async fetchTournamentCatalog(page: number = 1) {
    if (
      page <= this.tournamentCatalogCurrentPage ||
      !this.tournamentCatalogHasMorePage ||
      this.tournamentCatalogLoading
    ) {
      return
    }

    this.context.commit('SET_TOURNAMENT_CATALOG_CURRENT_PAGE', page)
    this.context.commit('SET_TOURNAMENT_CATALOG_LOADING')
    if (this.joinedTournaments.length <= 0 && this.userHasLoggedIn) {
      await this.context.dispatch('updateJoinedTournaments')
    }

    const response = await TournamentAPI.getTournaments(
      page,
      this.tournamentCatalogSearchFilter
    )
    const payload = response.data.payload
    this.context.commit('SET_TOURNAMENT_CATALOG', payload)
  }

  get userHasLoggedIn(): boolean {
    return this.context.rootGetters['user/hasLoggedIn'] || false
  }

  @Action({ rawError: true })
  async updateJoinedTournaments() {
    try {
      const response = await TournamentAPI.getMemberJoinedTournaments()
      const payload = response.data.payload

      if (response.data.status === ResponseStatus.Ok && payload) {
        this.context.commit('SET_JOINED_TOURNAMENTS', payload)
      }
    } catch {}
  }

  @Action({ rawError: true })
  async createTournamentTeam(request: {
    tournamentTeam: CreateTournamentTeamRequest
    tournamentId: string
    logoFile: File | Blob | null
  }) {
    try {
      const response = await TournamentAPI.createTournamentTeam(
        request.tournamentId,
        request.tournamentTeam,
        request.logoFile
      )
      const { payload, status } = response.data
      // return tournament team
      return payload
    } catch (error) {
      throw error
    }
  }

  @Action({ rawError: true })
  async getMemberJoinedTournamentTeam(tournamentId: string) {
    this.context.commit('SET_JOINED_TOURNAMENT_TEAM_LOADING', true)
    try {
      const member = this.context.rootState.user.member

      // skip if not logged in
      if (!member || !tournamentId) {
        return
      }
      const memberId = member!.memberId
      const response = await TournamentAPI.getMemberJoinedTournamentTeam(
        memberId,
        tournamentId
      )
      const payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok) {
        if (payload) {
          this.context.commit('SET_SELECTED_TOURNAMENT_TEAM', payload)
        } else {
          this.context.commit('SET_SELECTED_TOURNAMENT_TEAM', null)
        }
      }
      return response
    } catch (error) {
      this.context.commit('SET_SELECTED_TOURNAMENT_TEAM', null)
      throw error
    } finally {
      this.context.commit('SET_JOINED_TOURNAMENT_TEAM_LOADING', false)
    }
  }

  @Action({ rawError: true })
  async getTournamentTeams(data: { tournamentId: string; page: number }) {
    try {
      const response = await TournamentAPI.getTournamentTeams(
        data.tournamentId,
        data.page
      )
      const payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok && payload) {
        return payload
      }
      return false
    } catch {}
  }

  @Action({ rawError: true })
  async removeTournamentTeamMember(data: { teamId: string; memberId: string }) {
    try {
      const response = await TournamentAPI.removeTeamMember(
        data.teamId,
        data.memberId
      )
      if (response.data.status === ResponseStatus.Ok) {
        return true
      }
    } catch (error) {
      throw error
    }
  }

  @Action({ rawError: true })
  async setSelectedTournamentTeams(data: {
    tournamentId: string
    page: number
  }) {
    try {
      const response = await TournamentAPI.getTournamentTeams(
        data.tournamentId,
        data.page
      )
      const payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok && payload) {
        this.context.commit('SET_SELECTED_TOURNAMENT_TEAM_LIST', payload)
      }
    } catch (error) {
      throw error
    }
  }

  @Action
  resetSelectedTournamentTeams() {
    this.context.commit('RESET_SELECTED_TOURNAMENT_TEAM_LIST')
  }

  @Action
  resetSelectedTournamentTeam() {
    this.context.commit('RESET_SELECTED_TOURNAMENT_TEAM')
  }

  @Action
  resetSelectedTournament() {
    this.context.commit('SET_SELECTED_TOURNAMENT', null)
  }

  @Action({ rawError: true })
  async setSelectedTournament(tournamentId: string) {
    try {
      const response = await TournamentAPI.getTournament(tournamentId)
      const payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok && payload) {
        this.context.commit('SET_SELECTED_TOURNAMENT', payload)
      }
    } catch (error) {
      console.error(
        `Failed to get tournament ${tournamentId} with error`,
        error
      )
      throw error
    }
  }

  @Action({ rawError: true })
  async getTournamentRegisteredTeamCount(tournamentId: string) {
    try {
      this.context.commit('SET_TOURNAMENT_REGISTERED_TEAM_COUNT', -1)
      const response = await TournamentAPI.getRegisteredTeamCount(tournamentId)
      const payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok) {
        this.context.commit('SET_TOURNAMENT_REGISTERED_TEAM_COUNT', payload)
      }
    } catch {
      this.context.commit('SET_TOURNAMENT_REGISTERED_TEAM_COUNT', 0)
    }
  }

  @Action({ rawError: true })
  async confirmTournamentTeamRoster(request: {
    tournamentId: string
    tournamentTeamId: string
    confirmTournamentTeamRosterRequest: ConfirmTournamentTeamRosterRequest
  }) {
    try {
      const response = await TournamentAPI.confirmTournamentTeamRoster(
        request.tournamentId,
        request.tournamentTeamId,
        request.confirmTournamentTeamRosterRequest
      )

      if (response.data.status !== ResponseStatus.Ok) {
        return false
      }

      switch (response.data.payload!.status) {
        case ConfirmTournamentTeamRosterStatus.Success:
          this.context.commit('SET_CONFIRMED_TOURNAMENT_TEAM_ROSTER', true)
          return true

        case ConfirmTournamentTeamRosterStatus.Delayed:
          const cancellationToken = new CancellationToken()

          try {
            if (
              !(await checkTransactionStatus(
                response.data.payload!.walletTransactionId,
                cancellationToken
              ))
            )
              throw new Error('CheckTransactionStatus is failure')
          } catch (err) {
            console.log('[PAYMENT] Long polling failed!', err)
            return false
          }

          return true

        case ConfirmTournamentTeamRosterStatus.PaymentMethodRequired:
          const paymentModule = getModule(paymentStore, store)
          paymentModule.showTournamentEntryFeePaymentDialog({
            fee: response.data.payload!.fee,
            callbacks: {
              async createOrder(paymentMethod: string) {
                const response = await TournamentAPI.confirmTournamentTeamRoster(
                  request.tournamentId,
                  request.tournamentTeamId,
                  {
                    ...request.confirmTournamentTeamRosterRequest,
                    paymentMethod
                  }
                )

                if (
                  response.data.status !== ResponseStatus.Ok ||
                  response.data.payload!.status !==
                    ConfirmTournamentTeamRosterStatus.RedirectToPaymentGateway
                ) {
                  console.error('Unknown server error.', response.data)
                  throw new Error('Unknown server error')
                }

                return {
                  orderId: response.data.payload!.paymentMethodOrderId,
                  walletTransactionId: response.data.payload!
                    .walletTransactionId
                }
              }
            }
          })

          break
        default:
          console.error('Unsupported status', response.data.payload)
          throw new Error('Unsupported status')
      }
    } catch (error) {
      throw error
    }
  }

  @Action({ rawError: true })
  async undoConfirmTournamentTeamRoster(tournamentTeamId: string) {
    try {
      const response = await TournamentAPI.undoTournamentTeamRosterConfirmation(
        tournamentTeamId
      )
      if (response.data.status === ResponseStatus.Ok) {
        this.resetConfirmedTournamentTeamRoster()
        return true
      } else return false
    } catch (error) {
      throw error
    }
  }

  @Action
  resetConfirmedTournamentTeamRoster() {
    this.context.commit('SET_CONFIRMED_TOURNAMENT_TEAM_ROSTER', false)
  }

  @Action
  async fetchMoreTournamentCatalog() {
    await this.context.dispatch(
      'fetchTournamentCatalog',
      this.tournamentCatalogCurrentPage + 1
    )
  }

  @Action({ rawError: true })
  async fetchTournamentStagesByTournamentId(tournamentId: string) {
    try {
      const response = await TournamentAPI.getTournamentStages(tournamentId)
      const payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok && payload) {
        this.context.commit('SET_SELECTED_TOURNAMENT_STAGES', payload)
      }
    } catch (err) {
      if (err instanceof ServerError && err.getStatusCode() === 403) {
        return
      }

      throw err
    }
  }

  @Action({ rawError: true })
  async fetchTeamTokenInformation(teamToken: string) {
    const res = await TournamentAPI.getTeamTokenInformation(teamToken)
    return res.data.payload!
  }

  @Action
  addPlayerToSelectedTournamentTeam(player: TournamentPlayerEntity) {
    this.context.commit('SET_SELECTED_TOURNAMENT_TEAM', {
      ...this.selectedTournamentTeam,
      players: [...this.selectedTournamentTeam!.players, player]
    })
  }

  @Action
  setSelectedTournamentTeamRegistered(selectedMemberIds: string[]) {
    const tournamentTeam = {
      ...this.selectedTournamentTeam,
      state: TournamentTeamState.Registered,
      players: this.selectedTournamentTeam!.players.map(player =>
        selectedMemberIds.indexOf(player.memberId) === -1
          ? player
          : { ...player, isStarter: true }
      )
    }
    this.context.commit('SET_SELECTED_TOURNAMENT_TEAM', tournamentTeam)
  }

  @Action
  setInstantMatchIntroductionDialogVisible(value: boolean) {
    this.context.commit(
      'SET_AUTOMATED_TOURNAMENT_INTRODUCTION_DIALOG_VISIBLE',
      value
    )
  }

  @Action
  setAutomatedTournamentRuleDialogVisible(value: boolean) {
    this.context.commit('SET_AUTOMATED_TOURNAMENT_RULE_DIALOG_VISIBLE', value)
  }

  @Action
  setAutomatedTournamentTeamListDialogVisible(value: boolean) {
    this.context.commit(
      'SET_AUTOMATED_TOURNAMENT_TEAM_LIST_DIALOG_VISIBLE',
      value
    )
  }

  @Action
  setAutomatedTournamentBracketDialogVisible(value: boolean) {
    this.context.commit(
      'SET_AUTOMATED_TOURNAMENT_BRACKET_DIALOG_VISIBLE',
      value
    )
  }

  // for the sake of god, don't call this outside onVisibilityChanged() of TournamentCheckInOverlay
  // change updateState() logic of TournamentCheckInOverlay to control overlay visibility
  @Action
  setTournamentCheckInOverlayVisible(value: boolean) {
    this.context.commit(
      'SET_AUTOMATED_TOURNAMENT_CHECK_IN_OVERLAY_VISIBLE',
      value
    )
  }

  @Action
  setTournamentRegistrationClosedDialogVisible(value: boolean) {
    this.context.commit(
      'SET_TOURNAMENT_REGISTRATION_CLOSED_DIALOG_VISIBLE',
      value
    )
  }

  get tournaments() {
    return this.tournamentCatalog
  }

  get currentPage() {
    return this.tournamentCatalogCurrentPage
  }
}
